import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../i18/config";
import { getWorkflowHooks, removeWorkflowHook, saveWorkflowHook, workflowHookUrl } from "../../api";
import * as route from "../../api/routes";
import BreadCrumb from "../../components/Common/BreadCrumb";
import InputSwitch from "../../components/InputSwitch";
import Loader from "../../components/Loader";
import { WorkflowHook } from "./types";
import { formatWorkflowDate } from "./format";
import ListCard from "./ui/ListCard";
import WorkflowTable from "./ui/WorkflowTable";
import RowActions, { actionsColumn } from "./ui/RowActions";
import FormPanel from "./ui/FormPanel";
import { ConfirmModal } from "./ui/Dialogs";

interface HookForm {
    uuid?: string;
    name: string;
    secret: string;
    getHeaders: boolean;
    getMethod: boolean;
    responseTimeout: number;
}

const blank: HookForm = { name: '', secret: '', getHeaders: false, getMethod: false, responseTimeout: 25 };

/** Automation → Incoming webhooks: URLs other systems call to start scenarios. */
const WebhookList = () => {
    const { t } = useTranslation(['workflow', 'menu', 'common']);
    const [items, setItems] = useState<WorkflowHook[] | null>(null);
    const [form, setForm] = useState<HookForm | null>(null);
    const [saving, setSaving] = useState(false);
    const [regenerating, setRegenerating] = useState<WorkflowHook | null>(null);
    const [removing, setRemoving] = useState<WorkflowHook | null>(null);

    const load = useCallback(() => {
        getWorkflowHooks().then((r: any) => setItems(r.items || [])).catch(() => setItems([]));
    }, []);

    useEffect(() => load(), [load]);

    const fail = (e: any) => toast.error(e?.message || t('workflow:actionFailed'));
    const copy = (hook: WorkflowHook) => navigator.clipboard?.writeText(workflowHookUrl(hook.path)).then(() => toast.success(t('workflow:copied')));

    const edit = (hook: WorkflowHook) => setForm({
        uuid: hook.uuid,
        name: hook.name,
        secret: '',
        getHeaders: !!hook.options?.getHeaders,
        getMethod: !!hook.options?.getMethod,
        responseTimeout: hook.options?.responseTimeout || 25,
    });

    const save = () => {
        if (!form || !form.name.trim()) {
            return;
        }
        setSaving(true);
        const data: any = {
            uuid: form.uuid,
            name: form.name.trim(),
            options: { getHeaders: form.getHeaders, getMethod: form.getMethod, responseTimeout: form.responseTimeout },
        };
        // An empty secret field on an existing hook means "keep it".
        if (!form.uuid || form.secret !== '') {
            data.secret = form.secret === '' ? null : form.secret;
        }
        saveWorkflowHook(data).then(() => { setForm(null); load(); }).catch(fail).finally(() => setSaving(false));
    };

    const regenerate = () => regenerating &&
        saveWorkflowHook({ uuid: regenerating.uuid, regenerateToken: true }).then(() => { setRegenerating(null); load(); }).catch(fail);

    const remove = () => removing &&
        removeWorkflowHook(removing.uuid).then(() => { setRemoving(null); load(); }).catch(fail);

    const columns = [
        actionsColumn((h: WorkflowHook) => (
            <RowActions actions={[
                { icon: 'ri-pencil-fill', label: t('common:edit'), onClick: () => edit(h), hidden: !h.actions?.canEdit },
                { icon: 'ri-file-copy-fill', label: t('common:copy'), onClick: () => copy(h) },
                { icon: 'ri-refresh-fill', label: t('workflow:regenerateUrl'), onClick: () => setRegenerating(h), hidden: !h.actions?.canEdit },
                'divider',
                { icon: 'ri-delete-bin-fill', label: t('common:delete'), onClick: () => setRemoving(h), hidden: !h.actions?.canRemove, danger: true },
            ]} />
        )),
        {
            name: t('workflow:name'),
            grow: 2,
            cell: (h: WorkflowHook) => <span className="fw-medium">{h.name}{h.hasSecret && <i className="ri-lock-line ms-1 text-muted" title={t('workflow:hasSecret')} />}</span>,
        },
        {
            name: t('workflow:url'),
            grow: 4,
            cell: (h: WorkflowHook) => (
                <div className="input-group input-group-sm my-1">
                    <input className="form-control font-monospace" readOnly value={workflowHookUrl(h.path)} />
                    <button type="button" className="btn btn-soft-secondary" title={t('common:copy')} onClick={() => copy(h)}><i className="ri-file-copy-line" /></button>
                </div>
            ),
        },
        {
            name: t('workflow:scenario'),
            grow: 2,
            cell: (h: WorkflowHook) => h.scenario
                ? <Link to={route.AUTOMATION_SCENARIO.replace(':uuid', h.scenario.uuid)}>{h.scenario.name}</Link>
                : <span className="text-muted">{t('workflow:notUsed')}</span>,
        },
        { name: t('workflow:lastRequest'), grow: 2, cell: (h: WorkflowHook) => <span>{formatWorkflowDate(h.lastRequestAt)} · {h.requestCount}</span> },
    ];

    return (
        <>
            <BreadCrumb title={t('menu:automationWebhooks')} pageTitle={t('menu:automation')} />
            <Row className="g-4 mb-3">
                <div className="col-sm-auto">
                    <button type="button" className="btn btn-success" onClick={() => setForm({ ...blank })}><i className="ri-add-line align-bottom me-1" />{t('workflow:newWebhook')}</button>
                </div>
            </Row>

            <ListCard title={t('menu:automationWebhooks')} filters={<p className="text-muted mb-0">{t('workflow:webhooksIntro')}</p>}>
                {items === null ? <Loader /> :
                    items.length === 0
                        ? <div className="text-center text-muted py-5"><i className="ri-links-line display-5 d-block mb-2" /><p className="fs-14 mb-0">{t('workflow:noWebhooks')}</p></div>
                        : <WorkflowTable columns={columns as any} data={items} />
                }
            </ListCard>

            <FormPanel
                show={form !== null}
                title={form?.uuid ? t('workflow:editWebhook') : t('workflow:newWebhook')}
                saving={saving}
                canSave={!!form?.name.trim()}
                onSave={save}
                onClose={() => setForm(null)}
            >
                {form && <>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="wf-hook-name">{t('workflow:name')}</label>
                        <input id="wf-hook-name" className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label" htmlFor="wf-hook-secret">{t('workflow:secret')}</label>
                        <input id="wf-hook-secret" className="form-control font-monospace" value={form.secret} placeholder={form.uuid ? t('workflow:secretKeep') : ''} onChange={(e) => setForm({ ...form, secret: e.target.value })} />
                        <div className="form-text">{t('workflow:secretHint')}</div>
                    </div>
                    <div className="mb-3">
                        <InputSwitch id="wf-hook-headers" className="form-switch-md" placeholder={t('workflow:getHeaders')}
                            value={form.getHeaders} onChange={() => setForm({ ...form, getHeaders: !form.getHeaders })} />
                    </div>
                    <div className="mb-3">
                        <InputSwitch id="wf-hook-method" className="form-switch-md" placeholder={t('workflow:getMethod')}
                            value={form.getMethod} onChange={() => setForm({ ...form, getMethod: !form.getMethod })} />
                    </div>
                    <div>
                        <label className="form-label" htmlFor="wf-hook-timeout">{t('workflow:responseTimeout')}</label>
                        <input id="wf-hook-timeout" type="number" min={1} max={25} className="form-control" value={form.responseTimeout} onChange={(e) => setForm({ ...form, responseTimeout: Math.max(1, Math.min(25, parseInt(e.target.value, 10) || 25)) })} />
                        <div className="form-text">{t('workflow:responseTimeoutHint')}</div>
                    </div>
                </>}
            </FormPanel>

            <ConfirmModal
                show={regenerating !== null}
                title={t('workflow:regenerateUrl')}
                message={t('workflow:confirmRegenerate')}
                onConfirm={regenerate}
                onClose={() => setRegenerating(null)}
            />
            <ConfirmModal
                show={removing !== null}
                title={t('common:delete')}
                message={t('workflow:confirmDeleteWebhook', { name: removing?.name })}
                confirmLabel={t('common:delete')}
                danger
                onConfirm={remove}
                onClose={() => setRemoving(null)}
            />
        </>
    );
};

export default WebhookList;
